import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useOrg } from '../../contexts/OrgContext';
import { usePermission } from '../../hooks/usePermission';
import {
  getTemplates,
  deleteTemplate,
  duplicateTemplate,
} from '../../services/templateService';
import { FormTemplate } from '@easydone/shared';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

export function TemplatesPage() {
  const { currentOrg } = useOrg();
  const navigate = useNavigate();
  const canManageTemplates = usePermission('templates.create');

  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<FormTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    loadTemplates();
  }, [currentOrg]);

  useEffect(() => {
    filterTemplates();
  }, [searchQuery, selectedCategory, templates]);

  const loadTemplates = async () => {
    if (!currentOrg) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const templatesData = await getTemplates(currentOrg.id);
      setTemplates(templatesData);
      setFilteredTemplates(templatesData);
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = () => {
    let filtered = templates;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(t => t.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        t =>
          t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          t.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          t.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredTemplates(filtered);
  };

  const handleDuplicate = async (template: FormTemplate) => {
    if (!currentOrg) return;

    const newName = prompt('Enter name for duplicated template:', `${template.name} (Copy)`);
    if (!newName) return;

    try {
      await duplicateTemplate(currentOrg.id, template.id, newName);
      loadTemplates();
    } catch (error) {
      console.error('Failed to duplicate template:', error);
      alert('Failed to duplicate template');
    }
  };

  const handleDelete = async (templateId: string) => {
    if (!currentOrg) return;
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      await deleteTemplate(currentOrg.id, templateId);
      loadTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert('Failed to delete template');
    }
  };

  // Get unique categories
  const categories = ['all', ...new Set(templates.map(t => t.category))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading templates...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Form Templates</h1>
          <p className="text-gray-600 mt-1">
            Create and manage inspection form templates
          </p>
        </div>
        {canManageTemplates && (
          <button
            onClick={() => navigate('/templates/new')}
            className="btn btn-primary flex items-center justify-center lg:justify-start flex-shrink-0"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Create Template
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="input w-full sm:w-64"
        >
          {categories.map(category => (
            <option key={category} value={category}>
              {category === 'all' ? 'All Categories' : category}
            </option>
          ))}
        </select>
      </div>

      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <div className="card text-center py-12">
          <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchQuery || selectedCategory !== 'all'
              ? 'No templates found'
              : 'No templates yet'}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchQuery || selectedCategory !== 'all'
              ? 'Try adjusting your filters'
              : 'Get started by creating your first inspection form template'}
          </p>
          {!searchQuery && selectedCategory === 'all' && canManageTemplates && (
            <button
              onClick={() => navigate('/templates/new')}
              className="btn btn-primary"
            >
              Create Template
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => (
            <div key={template.id} className="card hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <Link
                    to={`/templates/${template.id}`}
                    className="text-lg font-semibold text-gray-900 hover:text-primary-600"
                  >
                    {template.name}
                  </Link>
                  <p className="text-sm text-gray-600 mt-1">{template.category}</p>
                </div>
                {canManageTemplates && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => navigate(`/templates/${template.id}/edit`)}
                      className="p-1 text-gray-400 hover:text-primary-600"
                      title="Edit"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDuplicate(template)}
                      className="p-1 text-gray-400 hover:text-blue-600"
                      title="Duplicate"
                    >
                      <DocumentDuplicateIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(template.id)}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="Delete"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {template.description && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                  {template.description}
                </p>
              )}

              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>
                  {template.sections.length} {template.sections.length === 1 ? 'section' : 'sections'}
                </span>
                <span className="text-xs">v{template.version}</span>
              </div>

              {template.assetTypes && template.assetTypes.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    Asset Types: {template.assetTypes.join(', ')}
                  </p>
                </div>
              )}

              <div className="mt-3">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    template.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {template.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
